import {
  ClipboardCheck, FileCheck2, RefreshCcw, ReceiptText, ScrollText,
  BadgeCheck, PlugZap, ShieldX, BarChart3,
} from "lucide-react";

export const services = [
  {
    slug: "insurance-verification",
    icon: ClipboardCheck,
    title: "Insurance Verification (IV)",
    tagline: "Accurate eligibility. Fewer surprises at the front desk.",
    intro: [
      "Every clean claim starts before the appointment. When coverage is verified in advance, your estimates are right, your patients know their portion, and eligibility denials simply stop happening.",
      "ClariDent verifies your schedule ahead of each visit and documents everything where your team can find it — so verification becomes a habit, not a scramble.",
    ],
    items: ["Eligibility & benefits verification", "Coverage details", "Deductibles", "Frequency limitations", "Waiting periods", "Plan limitations"],
    steps: [
      { t: "Schedule reviewed in advance", d: "We pull your upcoming schedule and verify each patient's coverage before the date of service." },
      { t: "Full breakdowns documented", d: "Benefits breakdowns and eligibility records are saved to the patient's document center for your team." },
      { t: "Issues flagged early", d: "Anything that needs attention — termed plans, missing info, limitation conflicts — reaches your front desk before the patient arrives." },
    ],
    outcome: "Your front desk quotes with confidence, patients know their portion before treatment, and claims stop denying for eligibility reasons.",
    seo_title: "Dental Insurance Verification Services | ClariDent RCM",
    seo_description: "ClariDent verifies dental insurance eligibility and benefits before treatment — coverage details, deductibles, frequencies, and plan limitations documented in advance.",
  },
  {
    slug: "dental-billing",
    icon: FileCheck2,
    title: "Claims Submission",
    tagline: "Clean claims out within 24 hours — built to be paid the first time.",
    intro: [
      "Billing is the engine of your cash flow. Claims that are coded correctly, documented properly, and submitted daily get paid faster and deny less.",
      "Our dental-only billing team prepares, submits, corrects, and tracks every claim — working inside your existing practice management software.",
    ],
    items: ["Accurate claim preparation", "Electronic claim submission", "Claim corrections", "Claim tracking", "Clean claim management"],
    steps: [
      { t: "Claims built right", d: "Accurate coding and required documentation assembled from your clinical notes, x-rays, and charts." },
      { t: "Submitted daily", d: "Electronic submission within 24 hours of the date of service keeps your cash flow moving." },
      { t: "Tracked to acceptance", d: "Every claim is followed until the payer accepts it — corrections handled immediately when needed." },
    ],
    outcome: "A billing engine that runs daily, denies less, and never depends on one overworked team member.",
    seo_title: "Dental Billing Services & Clean Claim Management | ClariDent RCM",
    seo_description: "Accurate dental claim preparation, electronic submission within 24 hours, corrections, and claim tracking — dental-only billing by ClariDent RCM.",
  },
  {
    slug: "ar-follow-up",
    icon: RefreshCcw,
    title: "AR Follow-Up",
    tagline: "Every aging claim worked until it's paid.",
    intro: [
      "Money sitting in AR isn't revenue — it's a promise. The older a balance gets, the harder it is to collect, which is why consistent follow-up matters more than occasional catch-up.",
      "ClariDent works your aging report on a steady cadence: newest claims first for fast wins, oldest balances by dollar value so nothing quietly expires.",
    ],
    items: ["Insurance AR follow-up", "Aging AR management", "Outstanding claim follow-up", "Payment tracking", "Insurance correspondence"],
    steps: [
      { t: "Weekly aging reviews", d: "Every bucket — 0–30 through 90+ — is reviewed on a consistent weekly rhythm." },
      { t: "Direct payer follow-up", d: "Status calls, portal checks, and written correspondence until each claim resolves." },
      { t: "Documented next actions", d: "Every touched account gets a note and a follow-up date, so nothing sits untouched." },
    ],
    outcome: "The 90+ bucket stops growing, recoverable revenue actually gets recovered, and your AR report becomes predictable.",
    seo_title: "Dental AR Follow-Up & Aging AR Management | ClariDent RCM",
    seo_description: "Systematic dental AR follow-up on every aging bucket — outstanding claim tracking, payer correspondence, and aging AR management by ClariDent RCM.",
  },
  {
    slug: "payment-posting",
    icon: ReceiptText,
    title: "Payment Posting",
    tagline: "Every dollar posted accurately, the day it arrives.",
    intro: [
      "Unposted payments hide problems: denials you haven't seen, underpayments nobody caught, patient balances that aren't real. Accurate daily posting turns your reports into the truth.",
      "We post insurance and patient payments promptly and reconcile every account so your numbers always reflect reality.",
    ],
    items: ["Insurance payment posting", "ERA/EOB posting", "Patient payment posting", "Accurate account reconciliation"],
    steps: [
      { t: "Daily posting", d: "ERAs, EOBs, and EFTs posted promptly so denials and underpayments surface the same day." },
      { t: "Adjustments applied correctly", d: "Contractual adjustments and write-offs posted to the right categories — not dumped into one bucket." },
      { t: "Accounts reconciled", d: "Each account balanced after posting, keeping your AR and reports accurate." },
    ],
    outcome: "Reports you can trust, problems caught the day they happen, and month-end that closes without drama.",
    seo_title: "Dental Payment Posting & ERA/EOB Reconciliation | ClariDent RCM",
    seo_description: "Accurate dental payment posting — insurance ERA/EOB posting, patient payments, and account reconciliation handled daily by ClariDent RCM.",
  },
  {
    slug: "patient-statements-collections",
    icon: ScrollText,
    title: "Patient Statements & Collections",
    tagline: "Professional follow-up that collects balances and keeps relationships.",
    intro: [
      "Patient balances need a delicate touch: firm enough to get paid, respectful enough to keep the patient coming back. Consistency does both.",
      "ClariDent runs a structured statement and follow-up cadence under your practice's standards, so balances get collected without awkward front-desk confrontations.",
    ],
    items: ["Patient statement management", "Outstanding patient balance follow-up", "Patient account communication", "Collection workflow support"],
    steps: [
      { t: "Accurate statements on schedule", d: "Clear, correct statements go out on a consistent cadence your patients can rely on." },
      { t: "Respectful follow-up", d: "Outstanding balances followed up professionally through the patient's preferred communication channel." },
      { t: "Escalation with your approval", d: "Accounts that need next steps are surfaced to your team — decisions always stay with the practice." },
    ],
    outcome: "Patient AR shrinks steadily while your practice's reputation for professionalism stays intact.",
    seo_title: "Dental Patient Statements & Collections Support | ClariDent RCM",
    seo_description: "Professional dental patient statements, balance follow-up, and collection workflow support that protects patient relationships — by ClariDent RCM.",
  },
  {
    slug: "credentialing",
    icon: BadgeCheck,
    title: "Credentialing",
    tagline: "Provider enrollment handled carefully, kept current.",
    intro: [
      "Credentialing errors don't just delay enrollment — they quietly block claims from ever being payable. Keeping provider information accurate across payers is unglamorous work with outsized impact.",
      "ClariDent manages credentialing and recredentialing cycles with careful documentation and persistent payer follow-up.",
    ],
    items: ["Provider credentialing", "Recredentialing", "Payer enrollment support", "Provider information maintenance"],
    steps: [
      { t: "Applications prepared & submitted", d: "Complete, accurate credentialing applications assembled and filed with your payers." },
      { t: "Followed until approved", d: "Persistent status follow-up so applications don't stall in a payer queue." },
      { t: "Records maintained", d: "Provider details, recredentialing dates, and payer records kept current and organized." },
    ],
    outcome: "Providers stay enrolled, claims stay payable, and recredentialing deadlines stop being emergencies.",
    seo_title: "Dental Provider Credentialing & Recredentialing | ClariDent RCM",
    seo_description: "Dental credentialing support: provider credentialing, recredentialing, payer enrollment, and provider information maintenance by ClariDent RCM.",
  },
  {
    slug: "eft-era-enrollment",
    icon: PlugZap,
    title: "EFT / ERA Enrollment",
    tagline: "Get paid electronically — faster, traceable, and easier to post.",
    intro: [
      "Paper checks and mailed EOBs slow everything down. Electronic funds transfer and electronic remittance advice move your money faster and make every payment easier to post and track.",
      "We handle the payer-by-payer enrollment process so your practice receives payments electronically wherever possible.",
    ],
    items: ["EFT enrollment", "ERA enrollment", "Payer setup support", "Payment delivery optimization"],
    steps: [
      { t: "Payer-by-payer enrollment", d: "EFT and ERA registrations completed with each of your payers, in the right portals." },
      { t: "Setup verified", d: "Enrollment confirmed and test remittances checked so nothing breaks mid-stream." },
      { t: "Delivery optimized", d: "Payment channels reviewed so more of your revenue arrives electronically over time." },
    ],
    outcome: "Faster deposits, cleaner remittance data, and less manual work at posting time.",
    seo_title: "Dental EFT & ERA Enrollment Services | ClariDent RCM",
    seo_description: "EFT and ERA enrollment with dental payers — faster electronic payments and remittances, set up and managed by ClariDent RCM.",
  },
  {
    slug: "denial-management",
    icon: ShieldX,
    title: "Denial Management",
    tagline: "Identify. Resolve. Prevent. Improve.",
    intro: [
      "A denial is delayed revenue with a reason code attached. Worked promptly with the right documentation, most are recoverable — and analyzed properly, they stop repeating.",
      "ClariDent works every denial to resolution and reports the trends, so your first-pass acceptance keeps improving month after month.",
    ],
    items: ["Denial identification", "Denial analysis", "Corrective action", "Appeal/follow-up support", "Denial trend reporting"],
    steps: [
      { t: "Every denial categorized", d: "Reason codes logged so patterns become visible instead of anecdotal." },
      { t: "Corrected & appealed", d: "Fixable claims corrected and resubmitted; appealable ones fought with documentation." },
      { t: "Root causes fixed", d: "Trend reporting shows whether the issue is verification, coding, or documentation — so it gets fixed upstream." },
    ],
    outcome: "Denied revenue gets recovered, and the same denials stop coming back.",
    seo_title: "Dental Denial Management & Appeal Support | ClariDent RCM",
    seo_description: "Dental denial management: identification, analysis, corrective action, appeals, and denial trend reporting — recover denied revenue with ClariDent RCM.",
  },
  {
    slug: "reporting-analytics",
    icon: BarChart3,
    title: "Reporting & Analytics",
    tagline: "Your revenue cycle, visible in real time.",
    intro: [
      "You shouldn't have to wait for a month-end report to know how your billing is performing. Clear, current numbers turn billing from a black box into a dashboard.",
      "ClariDent provides structured reporting across AR, collections, denials, and production — so decisions are made on evidence, not impressions.",
    ],
    items: ["AR reports", "Collection reporting", "Denial reporting", "Production/collection insights", "Revenue cycle performance reporting"],
    steps: [
      { t: "Structured, recurring reports", d: "AR aging, collections, and denial summaries delivered on a consistent schedule." },
      { t: "Metrics that matter", d: "Collection rate, denial rate, and days-in-AR tracked so trends are obvious early." },
      { t: "Reviewed together", d: "Regular performance reviews where we walk your numbers with you and plan next actions." },
    ],
    outcome: "Complete visibility into every claim, payment, and outstanding balance — and the confidence to make decisions on your numbers.",
    seo_title: "Dental RCM Reporting & Analytics | ClariDent RCM",
    seo_description: "Real-time dental revenue cycle reporting: AR reports, collection and denial analytics, and performance reviews from ClariDent RCM.",
  },
];

export const getService = (slug) => services.find((s) => s.slug === slug);
